import { cn } from '@iris/ui/lib/utils';
import { useId } from 'react';

const B_PATH =
  'M138 112h140c62 0 100 32 100 82c0 30-15 53-41 64c37 10 60 40 60 79c0 56-43 87-108 87H138c-11 0-20-9-20-20V132c0-11 9-20 20-20Zm62 74v66h68c24 0 38-12 38-33s-14-33-38-33h-68Zm0 132v72h80c27 0 43-13 43-36s-16-36-43-36h-80Z';
const SPARK = 'M386 98c3 16 9 23 26 30c-17 7-23 14-26 30c-3-16-9-23-26-30c17-7 23-14 26-30Z';

/**
 * ADR 0016: the Bonustiva logo — a white B on the purple gradient, with an orange coin and its spark ("votre fidélité
 * vous rapporte"). The same drawing as the apps' icon.svg. The name itself stays a translated text (brand.name).
 */
export function BrandMark({ className }: { className?: string }) {
  const id = useId().replace(/:/g, '');
  return (
    <svg
      aria-hidden
      viewBox="0 0 512 512"
      className={cn('inline-block h-8 w-8 shrink-0 rounded-[22%] shadow-sm', className)}
    >
      <defs>
        <linearGradient id={`${id}-bg`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#8B5CF6" />
          <stop offset="0.55" stopColor="#6D28D9" />
          <stop offset="1" stopColor="#4C1D95" />
        </linearGradient>
        <linearGradient id={`${id}-coin`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FDBA74" />
          <stop offset="0.5" stopColor="#F97316" />
          <stop offset="1" stopColor="#EA580C" />
        </linearGradient>
      </defs>
      <rect width="512" height="512" rx="116" fill={`url(#${id}-bg)`} />
      <g transform="translate(22 26) scale(0.92)">
        <path fill="#fff" fillRule="evenodd" d={B_PATH} />
      </g>
      <circle cx="386" cy="128" r="70" fill="#5B21B6" />
      <circle cx="386" cy="128" r="56" fill={`url(#${id}-coin)`} />
      <circle cx="386" cy="128" r="42" fill="none" stroke="#fff" strokeOpacity="0.45" strokeWidth="5" />
      <path fill="#fff" d={SPARK} />
    </svg>
  );
}

/**
 * ADR 0016: the full-screen splash while an app starts (session check). It continues the HTML splash of index.html
 * — same purple, same logo — and adds the name and the slogan.
 */
export function BrandSplash({ name, slogan, label }: { name: string; slogan: string; label: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-6 bg-[radial-gradient(circle_at_30%_20%,#8b5cf6_0%,#6d28d9_55%,#4c1d95_100%)] px-6 text-center text-white"
    >
      <span className="sr-only">{label}</span>
      <BrandMark className="h-24 w-24 animate-soft-breathe shadow-[0_18px_40px_-12px_rgb(30_0_60/0.55)] motion-reduce:animate-none" />
      <div className="space-y-1.5">
        <p className="text-3xl font-bold tracking-tight">{name}</p>
        <p className="text-sm text-white/80">{slogan}</p>
      </div>
      <span aria-hidden className="flex gap-2">
        {[0, 1, 2].map((dot) => (
          <i
            key={dot}
            className="h-2 w-2 animate-dot-bounce rounded-full bg-gold-400 motion-reduce:animate-none"
            style={{ animationDelay: `${dot * 150}ms` }}
          />
        ))}
      </span>
    </div>
  );
}
